import React from 'react';
import axios from 'axios';
import moment from 'moment-timezone';
import { useDashboard } from '../contexts/DashboardContext';
import SimpleCalendar from '../components/SimpleCalendar';

const CalendarPage = () => {
  const {
    appointments, blockForm, calculateAppointmentPricing, calendarEvents, calendarMonth,
    calendarView, dayDriveTimes, fetchCalendarData, fetchSettings, getPhotographerColor,
    loadingDriveTimes, orderFormSubmissions, photographerColors, photographerFilters,
    products, scheduleBlocks, selectedBlock, selectedPendingSubmission, setAppointmentClientSearchQuery,
    setAppointmentForm, setBlockForm, setCalendarMonth, setCalendarView, setEditingAppointment,
    setEditingBlock, setOrderFormSubmissions, setProposalNote, setProposedDate, setProposedTime,
    setRejectReason, setScheduleBlocks, setSelectedProducts, setShowAppointmentForm,
    setShowBlockDetails, setShowBlockModal, setShowPendingDetails, setShowProposeTimeModal,
    setShowRejectModal, showBlockDetails, showBlockModal, showPendingDetails, showToast,
    teamMembers, togglePhotographerFilter, accountSettings,
    API, editingBlock, setSelectedEvent, setShowEventDetails,
  } = useDashboard();

  return (
          <div className="h-full flex flex-col p-2">
            <div className="flex justify-between items-center mb-3 px-2" style={{ marginLeft: '60px' }}>
              <h2 className="text-2xl font-bold" >Calendar</h2>
              <div className="flex space-x-3">
                <button
                  onClick={async () => {
                    console.log('New Appointment button clicked');
                    // Ensure products and team members are loaded
                    if (products.length === 0 || teamMembers.length === 0) {
                      await fetchSettings();
                    }
                    setShowAppointmentForm(true);
                  }}
                  className="clara-btn-primary px-6 py-3"
                >
                  + New Appointment
                </button>
                <button
                  onClick={() => {
                    setEditingBlock(null);
                    setBlockForm({ title: 'Blocked', photographer_ids: [], start_date: '', start_time: '09:00', end_date: '', end_time: '17:00', all_day: false, reason: '' });
                    setShowBlockModal(true);
                  }}
                  className="clara-btn-secondary px-6 py-3"
                >
                  🚫 Block
                </button>
                <button
                  onClick={() => {
                    console.log('Refresh button clicked');
                    fetchCalendarData();
                  }}
                  className="clara-btn-secondary px-4 py-2"
                >
                  🔄 Refresh
                </button>
              </div>
            </div>

            {/* Block Schedule Modal */}
            {showBlockModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">{editingBlock ? 'Edit Block' : 'Block Time'}</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Title</label>
                      <input type="text" value={blockForm.title} onChange={(e) => setBlockForm({...blockForm, title: e.target.value})} className="clara-input w-full" placeholder="e.g., Vacation, Personal, etc." />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Photographers</label>
                      <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border rounded p-2">
                        {teamMembers.map(p => (
                          <label key={p.id} className="flex items-center text-sm">
                            <input type="checkbox" checked={blockForm.photographer_ids.includes(p.id)} onChange={(e) => {
                              const ids = e.target.checked ? [...blockForm.photographer_ids, p.id] : blockForm.photographer_ids.filter(id => id !== p.id);
                              setBlockForm({...blockForm, photographer_ids: ids});
                            }} className="mr-2" />
                            {p.name}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" checked={blockForm.all_day} onChange={(e) => setBlockForm({...blockForm, all_day: e.target.checked})} />
                      <label className="text-sm">All Day</label>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Start Date</label>
                        <input type="date" value={blockForm.start_date} onChange={(e) => setBlockForm({...blockForm, start_date: e.target.value, end_date: blockForm.end_date || e.target.value})} className="clara-input w-full" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">End Date</label>
                        <input type="date" value={blockForm.end_date} onChange={(e) => setBlockForm({...blockForm, end_date: e.target.value})} className="clara-input w-full" />
                      </div>
                    </div>
                    {!blockForm.all_day && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Start Time</label>
                          <input type="time" value={blockForm.start_time} onChange={(e) => setBlockForm({...blockForm, start_time: e.target.value})} className="clara-input w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">End Time</label>
                          <input type="time" value={blockForm.end_time} onChange={(e) => setBlockForm({...blockForm, end_time: e.target.value})} className="clara-input w-full" />
                        </div>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium mb-1">Reason (optional)</label>
                      <textarea value={blockForm.reason} onChange={(e) => setBlockForm({...blockForm, reason: e.target.value})} className="clara-input w-full" rows={2} placeholder="e.g., Out of office" />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-6">
                    <button onClick={() => setShowBlockModal(false)} className="clara-btn-secondary px-4 py-2">Cancel</button>
                    <button onClick={async () => {
                      try {
                        if (editingBlock) {
                          await axios.put(`${API}/schedule-blocks/${editingBlock.id}`, blockForm);
                        } else {
                          await axios.post(`${API}/schedule-blocks`, blockForm);
                        }
                        const res = await axios.get(`${API}/schedule-blocks`);
                        setScheduleBlocks(res.data);
                        setShowBlockModal(false);
                        showToast('Block saved successfully', 'success');
                      } catch (err) { showToast('Failed to save block', 'error'); }
                    }} className="clara-btn-primary px-4 py-2">{editingBlock ? 'Update' : 'Create'} Block</button>
                  </div>
                </div>
              </div>
            )}

            {/* Block Details Modal */}
            {showBlockDetails && selectedBlock && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                  <h3 className="text-lg font-semibold mb-4">🚫 {selectedBlock.title}</h3>
                  <div className="space-y-3 text-sm">
                    <p><strong>Date:</strong> {selectedBlock.start_date}{selectedBlock.end_date !== selectedBlock.start_date && ` - ${selectedBlock.end_date}`}</p>
                    {!selectedBlock.all_day && <p><strong>Time:</strong> {selectedBlock.start_time} - {selectedBlock.end_time}</p>}
                    {selectedBlock.all_day && <p><strong>All Day</strong></p>}
                    <p><strong>Photographers:</strong> {selectedBlock.photographer_ids?.map(id => teamMembers.find(m => m.id === id)?.name).filter(Boolean).join(', ') || 'None'}</p>
                    {selectedBlock.reason && <p><strong>Reason:</strong> {selectedBlock.reason}</p>}
                  </div>
                  <div className="flex justify-end gap-2 mt-6">
                    <button onClick={() => setShowBlockDetails(false)} className="clara-btn-secondary px-4 py-2">Close</button>
                    <button onClick={() => { setEditingBlock(selectedBlock); setBlockForm(selectedBlock); setShowBlockDetails(false); setShowBlockModal(true); }} className="clara-btn-secondary px-4 py-2">Edit</button>
                    <button onClick={async () => {
                      if (window.confirm('Delete this block?')) {
                        await axios.delete(`${API}/schedule-blocks/${selectedBlock.id}`);
                        const res = await axios.get(`${API}/schedule-blocks`);
                        setScheduleBlocks(res.data);
                        setShowBlockDetails(false);
                        showToast('Block deleted', 'success');
                      }
                    }} className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600">Delete</button>
                  </div>
                </div>
              </div>
            )}

            {/* Pending Booking Request Details Modal */}
            {showPendingDetails && selectedPendingSubmission && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
                  {/* Header */}
                  <div className="bg-amber-50 border-b border-amber-200 px-6 py-4 rounded-t-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                          <span className="text-xl">⏳</span>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Pending Booking Request</h3>
                          <p className="text-sm text-amber-700">Awaiting your approval</p>
                        </div>
                      </div>
                      <button 
                        onClick={() => setShowPendingDetails(false)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-6 space-y-4">
                    {/* Client Info */}
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{selectedPendingSubmission.client_name}</p>
                        <p className="text-sm text-gray-500">{selectedPendingSubmission.client_email}</p>
                        {selectedPendingSubmission.client_phone && (
                          <p className="text-sm text-gray-500">{selectedPendingSubmission.client_phone}</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Date & Time */}
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {moment(selectedPendingSubmission.scheduled_date).format('dddd, MMMM D, YYYY')}
                        </p>
                        <p className="text-sm text-gray-500">
                          {selectedPendingSubmission.scheduled_time} • {selectedPendingSubmission.duration_minutes || selectedPendingSubmission.duration || 60} minutes
                        </p>
                      </div>
                    </div>
                    
                    {/* Property Address */}
                    {selectedPendingSubmission.property_address && (
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{selectedPendingSubmission.property_address}</p>
                          {selectedPendingSubmission.property_sqft && (
                            <p className="text-sm text-gray-500">{selectedPendingSubmission.property_sqft.toLocaleString()} sq ft</p>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Photographer */}
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {selectedPendingSubmission.photographer_id 
                            ? (teamMembers.find(m => m.id === selectedPendingSubmission.photographer_id)?.name || 'Assigned Photographer')
                            : 'Any Available Photographer'}
                        </p>
                      </div>
                    </div>
                    
                    {/* Products */}
                    {selectedPendingSubmission.product_names && selectedPendingSubmission.product_names.length > 0 && (
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Services</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {selectedPendingSubmission.product_names.map((name, idx) => (
                              <span key={idx} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                                {name}
                              </span>
                            ))}
                            {selectedPendingSubmission.addon_names && selectedPendingSubmission.addon_names.map((name, idx) => (
                              <span key={`addon-${idx}`} className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                                + {name}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Price */}
                    {selectedPendingSubmission.total_price > 0 && (
                      <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                        <span className="text-gray-600">Total Price</span>
                        <span className="text-xl font-bold text-green-600">
                          ${selectedPendingSubmission.total_price.toFixed(2)}
                        </span>
                      </div>
                    )}
                    
                    {/* Special Instructions */}
                    {selectedPendingSubmission.special_instructions && (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-sm font-medium text-gray-700 mb-1">Special Instructions</p>
                        <p className="text-sm text-gray-600">{selectedPendingSubmission.special_instructions}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="bg-gray-50 px-6 py-4 rounded-b-xl flex justify-between items-center">
                    <button
                      onClick={() => setShowPendingDetails(false)}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      Close
                    </button>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setShowPendingDetails(false);
                          setShowRejectModal(true);
                          setRejectReason('');
                        }}
                        className="px-4 py-2 text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors font-medium"
                      >
                        Reject
                      </button>
                      <button
                        onClick={() => {
                          setShowPendingDetails(false);
                          setShowProposeTimeModal(true);
                          setProposedDate(selectedPendingSubmission?.scheduled_date || '');
                          setProposedTime('');
                          setProposalNote('');
                        }}
                        className="px-4 py-2 text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-lg transition-colors font-medium"
                      >
                        Propose New Time
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/order-form-submissions/${selectedPendingSubmission.id}/approve`);
                            setOrderFormSubmissions(prev => 
                              prev.map(s => s.id === selectedPendingSubmission.id ? { ...s, status: 'approved' } : s)
                            );
                            // Refresh appointments list
                            const token = localStorage.getItem('token');
                            const res = await axios.get(`${API}/appointments`, { headers: { Authorization: `Bearer ${token}` }});
                            setAppointments(res.data);
                            setShowPendingDetails(false);
                            showToast('Booking approved! Appointment created.', 'success');
                          } catch (err) {
                            showToast('Failed to approve', 'error');
                          }
                        }}
                        className="px-6 py-2 bg-green-600 text-white hover:bg-green-700 rounded-lg transition-colors font-medium"
                      >
                        Approve
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Photographer Filter */}
            <div className="bg-white rounded-lg shadow-sm border p-3 mb-3 mx-2" style={{ marginLeft: '60px' }}>
              <div className="flex items-center gap-4 flex-wrap">
                <span className="text-sm font-medium text-gray-600">Show:</span>
                {teamMembers
                  .filter(m => m.role?.toLowerCase() === 'photographer')
                  .map((photographer, index) => {
                    const color = photographerColors[index % photographerColors.length];
                    const isActive = photographerFilters[photographer.id] !== false;
                    return (
                      <button
                        key={photographer.id}
                        onClick={() => togglePhotographerFilter(photographer.id)}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                          isActive 
                            ? `${color.bg} ${color.text}` 
                            : 'bg-gray-100 text-gray-400'
                        }`}
                      >
                        <span className={`w-3 h-3 rounded-full ${isActive ? color.dot : 'bg-gray-300'}`}></span>
                        {photographer.name}
                        {isActive && (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    );
                  })}
              </div>
            </div>

            {/* Brand New Simple Calendar */}
            <div className="bg-white rounded-xl shadow-sm border p-2 flex-1">
              <SimpleCalendar 
                appointments={appointments}
                currentDate={calendarMonth}
                setCurrentDate={setCalendarMonth}
                photographerFilters={photographerFilters}
                getPhotographerColor={getPhotographerColor}
                calendarView={calendarView}
                setCalendarView={setCalendarView}
                pendingSubmissions={orderFormSubmissions.filter(s => s.status === 'pending_review')}
                dayDriveTimes={dayDriveTimes}
                loadingDriveTimes={loadingDriveTimes}
                calendarEvents={calendarEvents}
                scheduleBlocks={scheduleBlocks}
                teamMembers={teamMembers}
                accountSettings={accountSettings}
                products={products}
                setSelectedEvent={setSelectedEvent}
                setShowEventDetails={setShowEventDetails}
                onDateClick={(date) => {
                  console.log('Date clicked:', date);
                  setAppointmentForm(prev => ({
                    ...prev,
                    scheduled_date: date,
                    scheduled_time: '09:00'
                  }));
                  setEditingAppointment(null);
                  setShowAppointmentForm(true);
                }}
                onAppointmentClick={(appointment) => {
                  console.log('Appointment clicked:', appointment);
                  setEditingAppointment(appointment);
                  setAppointmentForm({
                    client_id: appointment.client_id,
                    client_name: appointment.client_name,
                    photographer_id: appointment.photographer_id,
                    photographer_name: appointment.photographer_name,
                    service_type: appointment.service_type,
                    property_address: appointment.property_address,
                    scheduled_date: appointment.scheduled_date,
                    scheduled_time: appointment.scheduled_time,
                    duration_minutes: appointment.duration_minutes,
                    notes: appointment.notes || '',
                    property_sqft: appointment.property_size || appointment.property_sqft || '',
                    selected_products: appointment.selected_products || []
                  });
                  setSelectedProducts(appointment.selected_products || []);
                  setAppointmentClientSearchQuery(appointment.client_name || '');
                  if (appointment.selected_products && appointment.selected_products.length > 0 && (appointment.property_size || appointment.property_sqft)) {
                    calculateAppointmentPricing(appointment.selected_products, appointment.property_size || appointment.property_sqft);
                  }
                  setShowAppointmentForm(true);
                }}
              />
            </div>
          </div>
  );
};

export default CalendarPage;
